import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
// Blocking POPIA modal. Mounts on every authenticated route. If the user has
// not yet accepted the current policy version, the modal blocks the screen
// until they accept (or log out). Bilingual via useTranslation.
export default function PopiaModal() {
    const { t } = useTranslation();
    const { accessToken, logout } = useAuthStore();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!accessToken)
            return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/popia/status');
                if (!cancelled)
                    setStatus(res.data);
            }
            catch {
                // If status fetch fails, assume accepted to avoid blocking the user
                // on transient network errors. Audit catches abuse.
                if (!cancelled)
                    setStatus({ accepted: true, acceptedAt: null, version: null, currentVersion: '1' });
            }
        })();
        return () => { cancelled = true; };
    }, [accessToken]);
    if (!accessToken || !status || status.accepted)
        return null;
    const accept = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.post('/popia/accept', { version: status.currentVersion });
            setStatus(res.data);
        }
        catch (e) {
            setError(e?.message || 'Could not save / Kon nie stoor nie');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsx("div", { role: "dialog", "aria-modal": "true", "aria-labelledby": "popia-modal-title", style: {
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
        }, children: _jsxs("div", { style: {
                background: 'var(--bg-secondary, #0f1d2e)', color: 'var(--text-primary, #e0f2fe)',
                maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto',
                borderRadius: 16, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }, children: [_jsx("h2", { id: "popia-modal-title", style: { fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 8 }, children: t('popia.modal.title') }), _jsx("p", { style: { marginTop: 0, marginBottom: 16, opacity: 0.85, fontSize: 14 }, children: t('popia.modal.intro') }), _jsx("p", { style: { fontWeight: 600, marginBottom: 8 }, children: t('popia.modal.declaration') }), _jsxs("ul", { style: { paddingLeft: 18, marginTop: 0, fontSize: 14, lineHeight: 1.5 }, children: [_jsx("li", { style: { marginBottom: 6 }, children: t('popia.modal.p1') }), _jsx("li", { style: { marginBottom: 6 }, children: t('popia.modal.p2') }), _jsx("li", { style: { marginBottom: 6 }, children: t('popia.modal.p3') }), _jsx("li", { style: { marginBottom: 6 }, children: t('popia.modal.p4') })] }), _jsxs("div", { style: {
                        background: 'rgba(255,255,255,0.05)', padding: 12, borderRadius: 8,
                        fontSize: 14, marginTop: 12, marginBottom: 16,
                    }, children: [_jsx("p", { style: { fontWeight: 600, marginTop: 0, marginBottom: 6 }, children: t('popia.modal.simpleHeading') }), _jsx("p", { style: { margin: 0 }, children: t('popia.modal.simpleBody') })] }), _jsxs("label", { style: {
                        display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14,
                        padding: 10, borderRadius: 8, cursor: 'pointer',
                        background: agreed ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.05)',
                    }, children: [_jsx("input", { type: "checkbox", checked: agreed, onChange: (e) => setAgreed(e.target.checked), style: { marginTop: 3, width: 18, height: 18, accentColor: '#22c55e' } }), _jsx("span", { children: t('popia.modal.agreeCheckbox') })] }), error && (_jsx("p", { role: "alert", style: { color: '#fca5a5', fontSize: 13, marginTop: 12 }, children: error })), _jsxs("div", { style: { display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }, children: [_jsx("button", { type: "button", onClick: accept, disabled: !agreed || loading, style: {
                                flex: 1, minWidth: 220, padding: '14px 20px',
                                background: agreed ? '#22c55e' : 'rgba(34,197,94,0.4)',
                                color: '#fff', border: 'none', borderRadius: 12,
                                fontWeight: 600, fontSize: 15,
                                cursor: agreed && !loading ? 'pointer' : 'not-allowed',
                                minHeight: 44,
                            }, children: loading ? '…' : t('popia.modal.acceptBtn') }), _jsx("button", { type: "button", onClick: () => logout(), style: {
                                flex: 1, minWidth: 160, padding: '14px 20px',
                                background: 'transparent', color: 'inherit',
                                border: '1px solid rgba(255,255,255,0.2)', borderRadius: 12,
                                fontWeight: 500, fontSize: 14, cursor: 'pointer', minHeight: 44,
                            }, children: t('popia.modal.declineBtn') })] })] }) }));
}
